import { useState } from 'react'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { useSelector } from 'react-redux'
import Box from '@mui/material/Box'
import ContentWrapper from 'components/common/ContentWrapper'
import styled from '@emotion/styled'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import { headerHeight, indexPageMaxContentWidth } from 'config/theme'
import { selectWebviewName } from 'store/webview/webviewSlice'
import { useSearchPanel } from 'components/common/PopupSearchPanel/hooks'
import MenuListC from 'components/common/MenuListC'
import { random } from 'lodash-es'
import { useGreetings, useCatalogsServiceMenus } from 'utils/api/hooks'
import { selectSelfConstruct } from 'store/selfConstruct/selfConstructSlice'

const DynamicSearchPanel = dynamic(
  () => import('components/common/PopupSearchPanel'),
  { ssr: false }
)

const DynamicMenuModalHorizontal = dynamic(
  () => import('./MenuModal/MenuModalHorizontal.js'),
  { ssr: true }
)

const DynamicNavButtonList = dynamic(() => import('./NavButtonList'), {
  ssr: true,
})

const DynamicSearchButton = dynamic(() => import('./SearchButton'), {
  ssr: false,
})

const HeaderWrap = styled(Box)`
  display: flex;
  align-items: center;
  height: ${headerHeight};
  max-width: ${indexPageMaxContentWidth};
  margin: auto;
`

const StyleContentWrapper = styled(ContentWrapper)`
  ${({ theme }) => `
    padding-left: ${theme.spacing(4)};
    padding-right: ${theme.spacing(4)};

    ${theme.breakpoints.up('md')} {
      padding-left: ${theme.spacing(4)};
      padding-right: ${theme.spacing(4)};
    }
    ${theme.breakpoints.up('lg')} {
      padding-left: ${theme.spacing(8)};
      padding-right: ${theme.spacing(8)};
    }
    ${theme.breakpoints.up('xl')} {
      padding-left: ${theme.spacing(12)};
      padding-right: ${theme.spacing(12)};
    }
  `}
`

const Logo = styled.img`
  cursor: pointer;
`

const Header = () => {
  const router = useRouter()
  const onLogoClick = () => {
    router.push('/')
  }

  const onCatalogOrSubMenuClick = (item) => {
    if (item.link) {
      if (item.linkTarget === 'blank') {
        window.open(item.link, '_blank')
      } else {
        router.push(item.link)
      }
    }
    setMenuModalOpen(false)
  }

  const onServiceClassificationClick = (item) => {
    const isServiceClassification = item.type === 'serviceClassification'

    if (isServiceClassification) {
      router.push(`/vender?serviceItem=${item?.serviceClassification?._id}`)
    } else {
      if (item.linkTarget === 'blank') {
        window.open(item.link, '_blank')
      } else {
        router.push(item.link)
      }
    }
  }

  const webviewName = useSelector(selectWebviewName)
  const selfConstruct = useSelector(selectSelfConstruct)

  const theme = useTheme()
  const isSmall = useMediaQuery(theme.breakpoints.down('lg'))

  const {
    searchPanelOpen,
    setSearchPanelOpen,
    onSearchPanelClose,
  } = useSearchPanel(webviewName)

  const onSearchIconClick = (event) => {
    event.stopPropagation()
    event.preventDefault()
    setSearchPanelOpen(!searchPanelOpen)
  }

  const { data: ServiceMenus } = useCatalogsServiceMenus({
    target: 'headerMenu',
  })

  // 主選單依序按照 sort 由大到小、createdAt 由新到舊排序
  if (Array.isArray(ServiceMenus)) {
    ServiceMenus.sort((a, b) => {
      if (b.sort !== a.sort) {
        return b.sort - a.sort
      }
      return new Date(b.createdAt) - new Date(a.createdAt)
    })
  }

  const { data: greetings } = useGreetings()
  const greetingsArray = greetings?.split('/') || []
  const greetingIndex = random(greetingsArray?.length - 1)

  const [menuModalOpen, setMenuModalOpen] = useState(false)
  const onMenuModalClose = () => setMenuModalOpen(false)

  if (webviewName === 'lsApp') {
    return (
      <>
        <DynamicSearchPanel
          open={searchPanelOpen}
          onClose={onSearchPanelClose}
        />
      </>
    )
  }

  return (
    <Box position="relative">
      <StyleContentWrapper $xxl={false} $xs={true} $isHomePage={true}>
        <HeaderWrap>
          <Box flexGrow={1} display="flex" alignItems="center">
            {isSmall && (
              <Box
                pr={3}
                py={4}
                display="flex"
                onClick={() => setMenuModalOpen(true)}
              >
                <img
                  width={24}
                  height={24}
                  src="/img/menu-icon.svg"
                  style={{ cursor: 'pointer' }}
                />
              </Box>
            )}
            <Logo
              src="/img/logo.svg"
              alt=""
              width={135}
              onClick={onLogoClick}
            />

            {!isSmall && (
              <DynamicNavButtonList
                catalogs={ServiceMenus}
                onCatalogOrSubMenuClick={onCatalogOrSubMenuClick}
                onServiceClassificationClick={onServiceClassificationClick}
              />
            )}
          </Box>
          <Box display="flex" alignItems="center">
            <Box mr={isSmall ? 1 : 3} display="inline-block">
              <DynamicSearchButton onSearchIconClick={onSearchIconClick} />
            </Box>

            <MenuListC greeting={greetingsArray[greetingIndex]}></MenuListC>
          </Box>
        </HeaderWrap>
      </StyleContentWrapper>

      <DynamicSearchPanel open={searchPanelOpen} onClose={onSearchPanelClose} />

      {isSmall && (
        <DynamicMenuModalHorizontal
          catalogs={ServiceMenus}
          open={menuModalOpen}
          onClose={onMenuModalClose}
          onCatalogOrSubMenuClick={onCatalogOrSubMenuClick}
          onServiceClassificationClick={onServiceClassificationClick}
        />
      )}
    </Box>
  )
}

export default Header
